import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import AppShell from "./AppShell";
import { API } from "../App";
import {
  Sparkles, FileText, Eye, EyeOff, Trash2, Edit3,
  Loader2, CheckCircle, AlertCircle, ArrowLeft,
  Plus, ExternalLink, Save, Lightbulb, Image as ImageIcon, Globe
} from "lucide-react";

const CATEGORIES = [
  "General",
  "Guides & How-Tos",
  "Business Tips",
  "Invoicing",
  "Freelancing",
  "Finance",
  "Product Updates",
];

function BlogList({ posts, loading, filter, setFilter, onEdit, onNew, onDelete, onTogglePublish, statusMsg }) {
  const filteredPosts = filter === "all" ? posts : posts.filter(p => p.status === filter);
  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[22px] sm:text-[26px] font-black text-[#0A0A0A] tracking-tight" data-testid="blog-admin-title">
            Blog Management
          </h1>
          <p className="text-[13px] text-[#6B7280] mt-0.5">{posts.length} article{posts.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onNew}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white text-[13px] font-bold px-4 py-2.5 flex items-center gap-2 transition-colors"
            data-testid="new-article-btn"
          >
            <Plus className="w-4 h-4" /> New Article
          </button>
        </div>
      </div>

      {/* Status Message */}
      {statusMsg && (
        <div className={`flex items-center gap-2 px-4 py-3 text-sm border ${statusMsg.type === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-green-50 border-green-200 text-green-700"}`} data-testid="blog-status-msg">
          {statusMsg.type === "error" ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {statusMsg.text}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { key: "all", label: "All" },
          { key: "published", label: "Published" },
          { key: "draft", label: "Draft" },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`text-[13px] font-semibold px-4 py-2 transition-colors border ${
              filter === tab.key
                ? "bg-[#F97316] text-white border-[#F97316]"
                : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
            }`}
            data-testid={`filter-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Posts Table */}
      <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-[#9CA3AF]">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading posts...
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="p-10 text-center">
            <FileText className="w-8 h-8 text-[#D1D5DB] mx-auto mb-3" />
            <p className="text-[#6B7280] text-[14px]">No {filter !== "all" ? filter : ""} articles yet.</p>
            <button onClick={onNew} className="text-[13px] text-[#F97316] font-semibold mt-2 hover:underline">
              Create your first article
            </button>
          </div>
        ) : (
          <table className="w-full text-left" data-testid="blog-posts-table">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wider">Title</th>
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wider hidden md:table-cell">Category</th>
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wider">Status</th>
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wider hidden sm:table-cell">Date</th>
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wider hidden sm:table-cell">Views</th>
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {filteredPosts.map(post => (
                <tr key={post.id} className="hover:bg-[#FAFAFA] transition-colors" data-testid={`blog-post-row-${post.id}`}>
                  <td className="px-5 py-4 max-w-[300px]">
                    <p className="text-[14px] font-semibold text-[#0A0A0A] truncate">{post.title}</p>
                    <p className="text-[12px] text-[#9CA3AF] truncate font-mono">{post.slug}</p>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className="text-[13px] text-[#6B7280]">{post.category || "General"}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-block text-[11px] font-bold px-2.5 py-1 rounded-full ${
                      post.status === "published"
                        ? "bg-[#ECFDF5] text-[#059669]"
                        : "bg-[#FFF7ED] text-[#EA580C]"
                    }`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell">
                    <span className="text-[13px] text-[#6B7280]">{formatDate(post.created_at)}</span>
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell">
                    <span className="text-[13px] text-[#6B7280] font-mono">{post.views || 0}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      {post.status === "published" && (
                        <>
                        <a
                          href={`/blog/${post.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 text-[#9CA3AF] hover:text-[#0A0A0A] transition-colors"
                          title="View live"
                          data-testid={`view-post-${post.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => onTogglePublish(post.id, "draft")}
                          className="p-2 text-[#9CA3AF] hover:text-[#F59E0B] transition-colors"
                          title="Unpublish"
                          data-testid={`unpublish-post-${post.id}`}
                        >
                          <EyeOff className="w-4 h-4" />
                        </button>
                        </>
                      )}
                      {post.status === "draft" && (
                        <button
                          onClick={() => onTogglePublish(post.id, "published")}
                          className="p-2 text-[#9CA3AF] hover:text-[#10B981] transition-colors"
                          title="Publish"
                          data-testid={`publish-post-${post.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onEdit(post.id)}
                        className="p-2 text-[#9CA3AF] hover:text-[#0A0A0A] transition-colors"
                        title="Edit"
                        data-testid={`edit-post-${post.id}`}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(post.id)}
                        className="p-2 text-[#9CA3AF] hover:text-[#EF4444] transition-colors"
                        title="Delete"
                        data-testid={`delete-post-${post.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BlogEditor({ postId, onBack, onSaved }) {
  const [post, setPost] = useState({
    title: "",
    content: "",
    category: "General",
    tags: "",
    featured_image_url: "",
    excerpt: "",
    meta_description: "",
    status: "draft",
  });
  const [loading, setLoading] = useState(!!postId);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [suggestingTopics, setSuggestingTopics] = useState(false);
  const [topicSuggestions, setTopicSuggestions] = useState([]);
  const [statusMsg, setStatusMsg] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [showUrlRewrite, setShowUrlRewrite] = useState(false);
  const [rewriteUrl, setRewriteUrl] = useState("");
  const [rewriteInstructions, setRewriteInstructions] = useState("");
  const [rewriting, setRewriting] = useState(false);

  useEffect(() => {
    if (postId) {
      axios.get(`${API}/blog/admin/posts/${postId}`).then(({ data }) => {
        setPost({
          title: data.title || "",
          content: data.content || "",
          category: data.category || "General",
          tags: (data.tags || []).join(", "),
          featured_image_url: data.featured_image_url || "",
          excerpt: data.excerpt || "",
          meta_description: data.meta_description || "",
          status: data.status || "draft",
        });
      }).catch(() => {
        setStatusMsg({ type: "error", text: "Failed to load post" });
      }).finally(() => setLoading(false));
    }
  }, [postId]);

  const wordCount = post.content ? post.content.split(/\s+/).filter(Boolean).length : 0;

  const handleSave = async (status) => {
    if (!post.title.trim()) {
      setStatusMsg({ type: "error", text: "Title is required" });
      return;
    }
    setSaving(true);
    setStatusMsg(null);
    try {
      const payload = {
        title: post.title,
        content: post.content,
        category: post.category,
        tags: post.tags.split(",").map(t => t.trim()).filter(Boolean),
        featured_image_url: post.featured_image_url || null,
        excerpt: post.excerpt,
        meta_description: post.meta_description,
        status: status || post.status,
      };
      if (postId) {
        await axios.put(`${API}/blog/posts/${postId}`, payload);
      } else {
        await axios.post(`${API}/blog/posts`, payload);
      }
      setStatusMsg({ type: "success", text: status === "published" ? "Published!" : "Saved as draft" });
      setTimeout(() => onSaved(), 1000);
    } catch (e) {
      setStatusMsg({ type: "error", text: e.response?.data?.detail || "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  const suggestTopics = async () => {
    setSuggestingTopics(true);
    try {
      const { data } = await axios.post(`${API}/blog/generate-ideas`, { niche: "freelancing, invoicing, and small business" });
      setTopicSuggestions(data.ideas || []);
    } catch (e) {
      setStatusMsg({ type: "error", text: "Failed to suggest topics" });
    } finally {
      setSuggestingTopics(false);
    }
  };

  const aiGenerate = async () => {
    if (!post.title.trim()) {
      setStatusMsg({ type: "error", text: "Enter a title first, then AI Generate" });
      return;
    }
    setGenerating(true);
    setStatusMsg(null);
    try {
      const { data } = await axios.post(`${API}/blog/generate-post`, {
        title: post.title,
        keywords: post.tags.split(",").map(t => t.trim()).filter(Boolean),
        generate_image: true,
      });
      setPost(prev => ({
        ...prev,
        content: data.content || prev.content,
        excerpt: data.excerpt || prev.excerpt,
        meta_description: data.meta_description || prev.meta_description,
        tags: (data.tags || []).join(", ") || prev.tags,
        category: data.category || prev.category,
      }));
      setStatusMsg({ type: "success", text: "Content generated! Review and publish." });
    } catch (e) {
      setStatusMsg({ type: "error", text: e.response?.data?.detail || "AI generation failed" });
    } finally {
      setGenerating(false);
    }
  };

  const aiGenerateImage = async () => {
    if (!post.title.trim()) {
      setStatusMsg({ type: "error", text: "Add a title first" });
      return;
    }
    setGeneratingImage(true);
    try {
      const { data } = await axios.post(`${API}/blog/generate-post`, {
        title: post.title,
        keywords: [],
        generate_image: true,
      });
      if (data.thumbnail_base64) {
        setPost(prev => ({ ...prev, featured_image_url: `data:image/png;base64,${data.thumbnail_base64}` }));
        setStatusMsg({ type: "success", text: "Image generated!" });
      }
    } catch (e) {
      setStatusMsg({ type: "error", text: "Image generation failed" });
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleUrlRewrite = async () => {
    if (!rewriteUrl.trim()) {
      setStatusMsg({ type: "error", text: "Paste a URL first" });
      return;
    }
    setRewriting(true);
    setStatusMsg(null);
    try {
      const { data } = await axios.post(`${API}/blog/rewrite-from-url`, {
        url: rewriteUrl,
        instructions: rewriteInstructions,
      });
      setPost(prev => ({
        ...prev,
        title: data.title || prev.title,
        content: data.content || prev.content,
        excerpt: data.excerpt || prev.excerpt,
        meta_description: data.meta_description || prev.meta_description,
        tags: (data.tags || []).join(", ") || prev.tags,
        category: data.category || prev.category,
      }));
      setShowUrlRewrite(false);
      setRewriteUrl("");
      setRewriteInstructions("");
      setStatusMsg({ type: "success", text: "Article rewritten in VeroBill's voice! Review and publish." });
    } catch (e) {
      setStatusMsg({ type: "error", text: e.response?.data?.detail || "Rewrite failed" });
    } finally {
      setRewriting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#6B7280]" />
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Top Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-[13px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors font-medium"
          data-testid="back-to-posts"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Posts
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={suggestTopics}
            disabled={suggestingTopics}
            className="text-[12px] font-semibold px-3 py-2 border border-[#E5E7EB] bg-white text-[#6B7280] hover:text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors flex items-center gap-1.5 disabled:opacity-50"
            data-testid="suggest-topics-btn"
          >
            {suggestingTopics ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5" />}
            Suggest Topics
          </button>
          <button
            onClick={aiGenerate}
            disabled={generating}
            className="text-[12px] font-semibold px-3 py-2 border border-[#E5E7EB] bg-white text-[#6B7280] hover:text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors flex items-center gap-1.5 disabled:opacity-50"
            data-testid="ai-generate-btn"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            AI Generate
          </button>
          <button
            onClick={() => setShowUrlRewrite(!showUrlRewrite)}
            className={`text-[12px] font-semibold px-3 py-2 border transition-colors flex items-center gap-1.5 ${showUrlRewrite ? "border-[#F97316] bg-[#FFF7ED] text-[#EA580C]" : "border-[#E5E7EB] bg-white text-[#6B7280] hover:text-[#0A0A0A] hover:border-[#0A0A0A]"}`}
            data-testid="rewrite-url-btn"
          >
            <Globe className="w-3.5 h-3.5" />
            Rewrite URL
          </button>
          <button
            onClick={() => handleSave("draft")}
            disabled={saving}
            className="text-[12px] font-semibold px-3 py-2 border border-[#E5E7EB] bg-white text-[#6B7280] hover:text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors flex items-center gap-1.5 disabled:opacity-50"
            data-testid="save-draft-btn"
          >
            <Save className="w-3.5 h-3.5" /> Save Draft
          </button>
          <button
            onClick={() => {
              if (post.title && post.content) {
                window.open(`/blog/preview?title=${encodeURIComponent(post.title)}`, "_blank");
              } else {
                setStatusMsg({ type: "error", text: "Add title and content to preview" });
              }
            }}
            className="text-[12px] font-semibold px-3 py-2 border border-[#E5E7EB] bg-white text-[#6B7280] hover:text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors flex items-center gap-1.5"
            data-testid="preview-btn"
          >
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
          <button
            onClick={() => handleSave("published")}
            disabled={saving}
            className="text-[12px] font-bold px-4 py-2 bg-[#F97316] text-white hover:bg-[#EA580C] transition-colors flex items-center gap-1.5 disabled:opacity-50"
            data-testid="publish-btn"
          >
            <FileText className="w-3.5 h-3.5" /> Publish
          </button>
        </div>
      </div>

      {/* Status Message */}
      {statusMsg && (
        <div className={`flex items-center gap-2 px-4 py-3 text-sm border mb-4 ${statusMsg.type === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-green-50 border-green-200 text-green-700"}`}>
          {statusMsg.type === "error" ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {statusMsg.text}
        </div>
      )}

      {/* Topic Suggestions */}
      {topicSuggestions.length > 0 && (
        <div className="bg-[#FFFBEB] border border-[#FDE68A] p-4 mb-4">
          <p className="text-[12px] font-bold text-[#92400E] uppercase tracking-wider mb-2">Topic Suggestions</p>
          <div className="space-y-2">
            {topicSuggestions.map((idea, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setPost(prev => ({ ...prev, title: idea.title, tags: (idea.keywords || []).join(", ") }));
                  setTopicSuggestions([]);
                }}
                className="block w-full text-left px-3 py-2 border border-[#FDE68A] bg-white hover:bg-[#FEF3C7] text-[13px] text-[#0A0A0A] transition-colors"
                data-testid={`topic-suggestion-${idx}`}
              >
                <span className="font-semibold">{idea.title}</span>
                {idea.excerpt && <span className="text-[#6B7280] ml-2">— {idea.excerpt}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* URL Rewrite Panel */}
      {showUrlRewrite && (
        <div className="bg-[#FFF7ED] border border-[#FDBA74] p-4 mb-4" data-testid="url-rewrite-panel">
          <p className="text-[12px] font-bold text-[#9A3412] uppercase tracking-wider mb-2">Rewrite from URL</p>
          <p className="text-[12px] text-[#C2410C] mb-3">Paste a competitor article URL. AI will rewrite it in VeroBill's voice with a fresh angle.</p>
          <div className="space-y-3">
            <input
              type="url"
              value={rewriteUrl}
              onChange={e => setRewriteUrl(e.target.value)}
              placeholder="https://example.com/blog/their-article"
              className="w-full border border-[#FDBA74] px-3 py-2 text-[13px] bg-white focus:outline-none focus:border-[#EA580C] transition-colors"
              data-testid="rewrite-url-input"
            />
            <input
              type="text"
              value={rewriteInstructions}
              onChange={e => setRewriteInstructions(e.target.value)}
              placeholder="Extra instructions (optional) — e.g. 'focus on freelancers'"
              className="w-full border border-[#FDBA74] px-3 py-2 text-[13px] bg-white focus:outline-none focus:border-[#EA580C] transition-colors"
              data-testid="rewrite-instructions-input"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={handleUrlRewrite}
                disabled={rewriting}
                className="bg-[#EA580C] text-white text-[12px] font-bold px-4 py-2 hover:bg-[#C2410C] transition-colors flex items-center gap-1.5 disabled:opacity-50"
                data-testid="rewrite-submit-btn"
              >
                {rewriting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Rewriting...</> : <><Sparkles className="w-3.5 h-3.5" /> Rewrite Article</>}
              </button>
              <button
                onClick={() => setShowUrlRewrite(false)}
                className="text-[12px] text-[#9A3412] hover:underline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Content Editor */}
        <div className="lg:col-span-2 space-y-4">
          {/* Title */}
          <input
            type="text"
            value={post.title}
            onChange={e => setPost(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Article title..."
            className="w-full text-[22px] font-bold text-[#0A0A0A] bg-transparent border-b-2 border-[#E5E7EB] focus:border-[#0A0A0A] outline-none pb-3 placeholder:text-[#D1D5DB] transition-colors"
            data-testid="article-title-input"
          />

          {/* Content Area */}
          <div className="bg-white border border-[#E5E7EB]">
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#E5E7EB] bg-[#FAFAFA]">
              <span className="text-[11px] font-bold text-[#6B7280] uppercase tracking-wider">Content (HTML)</span>
              <span className="text-[11px] text-[#9CA3AF]">{wordCount} words</span>
            </div>
            <textarea
              value={post.content}
              onChange={e => setPost(prev => ({ ...prev, content: e.target.value }))}
              placeholder={"Write your article in HTML format...\n<h2>Section Heading</h2>\n<p>Your content here...</p>"}
              className="w-full min-h-[400px] p-4 text-[14px] text-[#374151] font-mono leading-relaxed bg-white focus:outline-none resize-y"
              data-testid="article-content-input"
            />
          </div>
        </div>

        {/* Right: Sidebar */}
        <div className="lg:col-span-1 space-y-5">
          {/* Post Settings */}
          <div className="bg-white border border-[#E5E7EB] p-5">
            <h3 className="text-[14px] font-bold text-[#0A0A0A] mb-4">Post Settings</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] mb-1.5">Category</label>
                <select
                  value={post.category}
                  onChange={e => setPost(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-[13px] bg-white focus:outline-none focus:border-[#0A0A0A] transition-colors"
                  data-testid="article-category"
                >
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] mb-1.5">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={post.tags}
                  onChange={e => setPost(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="invoicing, freelance, tips"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-[13px] focus:outline-none focus:border-[#0A0A0A] transition-colors"
                  data-testid="article-tags"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[12px] font-semibold text-[#6B7280]">Featured Image</label>
                  <button
                    onClick={aiGenerateImage}
                    disabled={generatingImage}
                    className="text-[11px] text-[#F97316] font-semibold hover:underline flex items-center gap-1 disabled:opacity-50"
                    data-testid="ai-generate-image-btn"
                  >
                    {generatingImage ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                    AI Generate
                  </button>
                </div>
                <input
                  type="text"
                  value={post.featured_image_url}
                  onChange={e => setPost(prev => ({ ...prev, featured_image_url: e.target.value }))}
                  placeholder="https://... or click AI Generate"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-[13px] focus:outline-none focus:border-[#0A0A0A] transition-colors"
                  data-testid="article-featured-image"
                />
                {post.featured_image_url && (
                  <img src={post.featured_image_url} alt="Preview" className="mt-2 w-full h-24 object-cover border border-[#E5E7EB]" onError={(e) => { e.target.style.display = "none"; }} />
                )}
              </div>
            </div>
          </div>

          {/* SEO */}
          <div className="bg-white border border-[#E5E7EB] p-5">
            <h3 className="text-[14px] font-bold text-[#0A0A0A] mb-4">SEO</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] mb-1.5">Excerpt (shown in listings)</label>
                <textarea
                  value={post.excerpt}
                  onChange={e => setPost(prev => ({ ...prev, excerpt: e.target.value }))}
                  placeholder="Brief summary of the article..."
                  rows={3}
                  maxLength={200}
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-[13px] focus:outline-none focus:border-[#0A0A0A] resize-none transition-colors"
                  data-testid="article-excerpt"
                />
                <p className="text-[11px] text-[#9CA3AF] mt-1">{post.excerpt.length}/200 chars</p>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] mb-1.5">Meta Description</label>
                <textarea
                  value={post.meta_description}
                  onChange={e => setPost(prev => ({ ...prev, meta_description: e.target.value }))}
                  placeholder="SEO description for search engines..."
                  rows={3}
                  maxLength={155}
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-[13px] focus:outline-none focus:border-[#0A0A0A] resize-none transition-colors"
                  data-testid="article-meta-description"
                />
                <p className="text-[11px] text-[#9CA3AF] mt-1">{post.meta_description.length}/155 chars</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BlogAdmin() {
  const [view, setView] = useState("list"); // "list" or "editor"
  const [editPostId, setEditPostId] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [statusMsg, setStatusMsg] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/blog/admin/posts`);
      setPosts(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleEdit = (postId) => {
    setEditPostId(postId);
    setView("editor");
  };

  const handleNew = () => {
    setEditPostId(null);
    setView("editor");
  };

  const handleBack = () => {
    setView("list");
    setEditPostId(null);
    fetchPosts();
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await axios.delete(`${API}/blog/posts/${postId}`);
      setStatusMsg({ type: "success", text: "Post deleted" });
      fetchPosts();
    } catch (e) {
      setStatusMsg({ type: "error", text: "Failed to delete" });
    }
  };

  const handleTogglePublish = async (postId) => {
    try {
      const { data } = await axios.post(`${API}/blog/posts/${postId}/publish`);
      setStatusMsg({ type: "success", text: data.message });
      fetchPosts();
    } catch (e) {
      setStatusMsg({ type: "error", text: "Failed to toggle publish" });
    }
  };

  return (
    <AppShell active="blog">
      <div className="max-w-6xl mx-auto">
        {view === "list" ? (
          <BlogList
            posts={posts}
            loading={loading}
            filter={filter}
            setFilter={setFilter}
            onEdit={handleEdit}
            onNew={handleNew}
            onDelete={handleDelete}
            onTogglePublish={handleTogglePublish}
            statusMsg={statusMsg}
          />
        ) : (
          <BlogEditor
            postId={editPostId}
            onBack={handleBack}
            onSaved={handleBack}
          />
        )}
      </div>
    </AppShell>
  );
}
